import {
  ContentIcon,
  type ContentIconProps,
} from "@dropbox/dash-component-library";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { ComputerLine, DropboxLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { isAssistantFileUpload } from "@mirage/mosaics/Chat/utils/isAssistantFileUpload";
import {
  BrandedSiteIcon,
  DesktopFileIcon,
  FileTypeIcon,
  getSearchResultIconsWithTooltip,
  SearchResultIcon,
  UrlSrcIcon,
} from "@mirage/shared/icons";
import { isDesktopLocalFile } from "../search/search-result";
import styles from "./FileContentIcon.module.css";

import { FileIcon } from "@dropbox/dig-content-icons";
import type { Atoms } from "@dropbox/dig-foundations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Hover_DashMultimedia } from "@mirage/analytics/events/types/hover_dash_multimedia";
import { useMultimediaPreview } from "@mirage/mosaics/SearchPage/Multimedia/hooks/useMultimediaPreview";
import type { SummarizableResult } from "@mirage/mosaics/SummaryQnaPanel/utils";
import type { SearchResult } from "@mirage/service-dbx-api";
import type { RecentConnectorContent } from "@mirage/service-recent-content/types";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import React, { useCallback } from "react";
import type { ActivityResult } from "../feed/activity-result";

type IconContent =
  | SearchResult
  | RecentConnectorContent
  | Recommendation
  | ActivityResult
  | SummarizableResult;

export type Props = {
  content: IconContent;
  size?: ContentIconProps["size"];
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  hasBackground?: boolean;
  thumbnailUrl?: string;
  onThumbnailHover?: () => void;
  badgeTooltipTitle?: string;
  imageProps?: React.ImgHTMLAttributes<HTMLImageElement>;
};

// type guard for SearchResult
function isSearchResult(content: IconContent): content is SearchResult {
  return (content as SearchResult)?.recordType !== undefined;
}

function isSummarizableResult(
  content: IconContent,
): content is SummarizableResult {
  return (content as SummarizableResult)?.type !== undefined;
}

type IconProps = Pick<Props, "badge" | "content" | "icon">;
export function getIconProps({ icon, badge, content }: IconProps): {
  icon: React.ReactNode;
  badgeIcon?: React.ReactNode;
  shouldConstrainContent?: boolean;
} {
  if (icon) {
    return {
      icon: icon,
      badgeIcon: badge ?? <SearchResultIcon size="100%" result={content} />,
    };
  }

  if (content?.fileTypeInfo?.icon) {
    return {
      icon: <FileTypeIcon result={content} size="100%" />,
      badgeIcon: badge ?? <SearchResultIcon size="100%" result={content} />,
    };
  }

  if (!isSummarizableResult(content) && content?.brandedSiteInfo?.icon) {
    return {
      icon: <BrandedSiteIcon size="100%" result={content} />,
      badgeIcon: badge ?? <SearchResultIcon size="100%" result={content} />,
    };
  }

  if (
    isSearchResult(content) &&
    (isDesktopLocalFile(content) || isAssistantFileUpload(content))
  ) {
    return {
      icon: <DesktopFileIcon size="100%" result={content} />,
      badgeIcon: badge ?? (
        <UIIcon className={styles.badgeIcon} src={ComputerLine} />
      ),
      shouldConstrainContent: false,
    };
  }

  if (isSummarizableResult(content) && typeof content.icon === "string") {
    return {
      icon: (
        <UrlSrcIcon
          name={typeof content.title === "string" ? content.title : ""}
          iconUrl={content.icon}
          size="20"
        />
      ),
    };
  }

  if (isSearchResult(content) && content.recordType[".tag"] === "file") {
    const extension = content.title?.split(".").pop()?.toLowerCase();
    if (extension) {
      return {
        icon: <FileIcon extension={extension} size="small" />,
        badgeIcon: <UIIcon className={styles.badgeIcon} src={DropboxLine} />,
      };
    }
  }

  return {
    icon: <SearchResultIcon size="100%" result={content} />,
  };
}

export function FileContentIcon({
  content,
  size = "medium",
  icon,
  badge,
  /**
   * true shows the background, border and badge
   * false only shows the icon
   */
  hasBackground = true,
  thumbnailUrl = undefined,
  onThumbnailHover = undefined,
  badgeTooltipTitle = undefined,
  imageProps,
  constrainContent,
  contentPadding = undefined,
}: Props & { constrainContent?: boolean; contentPadding?: Atoms["padding"] }) {
  const {
    badgeIcon,
    icon: iconProp,
    shouldConstrainContent = constrainContent ?? true,
  } = getIconProps({ content, icon, badge });
  const iconsWithTooltip = getSearchResultIconsWithTooltip(
    badgeTooltipTitle || "",
    iconProp,
    badgeIcon,
    thumbnailUrl,
  );
  return (
    <ContentIcon
      thumbnailUrl={thumbnailUrl}
      onThumbnailHover={onThumbnailHover}
      imageProps={imageProps}
      icon={
        contentPadding ? (
          <Box padding={contentPadding} width="100%" height="100%">
            {iconsWithTooltip.icon}
          </Box>
        ) : (
          iconsWithTooltip.icon
        )
      }
      badgeIcon={iconsWithTooltip.badgeIcon}
      shouldConstrainContent={shouldConstrainContent}
      hasBackground={hasBackground}
      size={size}
    />
  );
}

interface FileContentIconWithThumbnailProps
  extends Omit<Props, "thumbnailUrl" | "onThumbnailHover"> {
  providedThumbnailUrl?: string;
  shouldFetchThumbnail?: boolean;
}

// wrapper around FileContentIcon that fetches the base64 image
// if previewUrl is available
export function FileContentIconWithThumbnail({
  content,
  providedThumbnailUrl,
  shouldFetchThumbnail,
  ...rest
}: FileContentIconWithThumbnailProps) {
  const contentPreviewUrl =
    "previewUrl" in content ? content.previewUrl : undefined;
  const { base64Image, base64Loading, hasError } = useMultimediaPreview(
    shouldFetchThumbnail ? contentPreviewUrl || undefined : undefined,
    512,
  );
  const thumbnailUrl =
    providedThumbnailUrl ||
    (base64Image && !base64Loading && !hasError ? base64Image : undefined);

  const { reportPapEvent, searchSessionManager } = useMirageAnalyticsContext();
  const logMultimediaIconHover = useCallback(() => {
    if (thumbnailUrl) {
      reportPapEvent(
        PAP_Hover_DashMultimedia({
          searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
        }),
      );
    }
  }, [thumbnailUrl, reportPapEvent, searchSessionManager]);
  return (
    <FileContentIcon
      content={content}
      thumbnailUrl={thumbnailUrl}
      onThumbnailHover={logMultimediaIconHover}
      {...rest}
    />
  );
}
